// src/pages/Profile.jsx
import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import Container from "../components/Container";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { updateProfile, logoutUser } from "../app/features/user/userSlice";
import { FiEdit, FiHeart } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import { IoChevronForward, IoEyeOutline } from "react-icons/io5";
import { BsBoxSeam } from "react-icons/bs";

import "./styles/profile.css";

// ✅ Products (for sorting page /product?sort=recentlyReviewed)
import { getAllProducts } from "../app/features/products/productSlice";

/* ========================= HELPERS (match Home) ========================= */
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const formatUGX = (n) => `UGX ${toNum(n).toLocaleString()}`;

const getFirstImageUrl = (images) => {
  if (!images) return "";
  if (Array.isArray(images)) {
    const first = images[0];
    if (!first) return "";
    if (typeof first === "string") return first;
    return first?.url || "";
  }
  return "";
};

const getProductPath = (slugOrId) => `/product/${encodeURIComponent(String(slugOrId || ""))}`;

const resolvePriceUI = (p = {}, fallbackPrice = 0) => {
  const price = toNum(p?.price ?? fallbackPrice);
  const disc = toNum(p?.discountedPrice);

  const hasDiscount = disc > 0 && price > 0 && disc < price;

  if (hasDiscount) {
    return {
      now: disc,
      old: price,
      priceText: `UGX (${disc}-${price})`,
      oldText: "",
      isDiscount: true,
    };
  }

  return {
    now: price,
    old: 0,
    priceText: formatUGX(price),
    oldText: "",
    isDiscount: false,
  };
};

const parseUGX = (v) => {
  if (v == null) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;

  const s = String(v);
  const nums = s.match(/\d[\d,]*/g);
  if (!nums || !nums.length) return 0;
  return Number(nums[0].replace(/,/g, "")) || 0;
};

/* ========================= ImgWithLoader (same behavior style) ========================= */
const ImgWithLoader = ({
  src,
  alt = "",
  className = "",
  wrapClassName = "",
  fallbackSrc = "/images/placeholder.png",
  style,
  imgStyle,
  loaderSize = 20,
  loaderClassName = "",
  onClick,
}) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const imgRef = useRef(null);

  const hasSrc = Boolean(String(src || "").trim());
  const finalSrc = !hasSrc || failed ? fallbackSrc : src;

  useEffect(() => {
    setLoaded(false);
    setFailed(false);
  }, [finalSrc]);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    if (img.complete && img.naturalWidth > 0) setLoaded(true);
  }, [finalSrc]);

  return (
    <div
      className={`imgload-wrap ${wrapClassName}`}
      style={{ position: "relative", ...style }}
      onClick={onClick}
    >
      {!loaded && (
        <div
          className={`imgload-spinner ${loaderClassName}`}
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            zIndex: 2,
            pointerEvents: "none",
          }}
          aria-hidden="true"
        >
          <div
            className="imgload-spin"
            style={{
              width: loaderSize,
              height: loaderSize,
              borderRadius: "50%",
              border: "2px solid rgba(0,0,0,0.18)",
              borderTopColor: "rgba(0,0,0,0.55)",
              animation: "imgspin 0.8s linear infinite",
            }}
          />
        </div>
      )}

      <img
        key={finalSrc}
        ref={imgRef}
        src={finalSrc}
        alt={alt}
        className={className}
        style={{
          display: "block",
          width: "100%",
          opacity: loaded ? 1 : 0,
          transition: "opacity 180ms ease",
          ...imgStyle,
        }}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => {
          if (hasSrc && !failed) setFailed(true);
          setLoaded(true);
        }}
      />
    </div>
  );
};

// add keyframes once (safe)
const ensureSpinStyle = (() => {
  let done = false;
  return () => {
    if (done || typeof document === "undefined") return;
    done = true;
    const style = document.createElement("style");
    style.innerHTML = `
      @keyframes imgspin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }
    `;
    document.head.appendChild(style);
  };
})();

const profileSchema = yup.object({
  firstname: yup.string().required("First Name is required"),
  lastname: yup.string().required("Last Name is required"),
  email: yup.string().email("Email should be valid").required("Email Address is required"),
  mobile: yup.string().required("Mobile No. is required"),
});

const Profile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    ensureSpinStyle();
  }, []);

  const userState = useSelector((state) => state.auth.user);
  const authLoading = useSelector((state) => state.auth.isLoading);

  const [edit, setEdit] = useState(true);
  const [logoutBusy, setLogoutBusy] = useState(false);

  // ✅ refs for scrolling + focusing
  const editSectionRef = useRef(null);
  const firstNameRef = useRef(null);

  // ✅ slider refs (rows)
  const rvTrackRef = useRef(null); // Recently Viewed

  // ✅ Load products once (kept because you navigate to /product?sort=recentlyReviewed)
  useEffect(() => {
    dispatch(getAllProducts());
  }, [dispatch]);

  /* ========================= ✅ RECENTLY VIEWED (MATCH HOME) ========================= */
  const getRecentlyViewed = () => {
    try {
      const raw = JSON.parse(localStorage.getItem("recentlyViewed") || "[]");
      return Array.isArray(raw) ? raw : [];
    } catch {
      return [];
    }
  };

  const [recentlyViewed, setRecentlyViewed] = useState(() => getRecentlyViewed());

  useEffect(() => {
    const update = () => setRecentlyViewed(getRecentlyViewed());
    window.addEventListener("recentlyViewedUpdated", update);
    window.addEventListener("storage", update);
    return () => {
      window.removeEventListener("recentlyViewedUpdated", update);
      window.removeEventListener("storage", update);
    };
  }, []);

  const RECENT_VIEWED_UI = useMemo(() => {
    const arr = Array.isArray(recentlyViewed) ? recentlyViewed : [];

    return arr
      .sort((a, b) => (b?.viewedAt || 0) - (a?.viewedAt || 0))
      .slice(0, 12)
      .map((it) => {
        const id = it?.id || it?._id || it?.productId;
        const slug = String(it?.slug || it?.productSlug || "").trim();

        if (it?.raw) {
          const pr = resolvePriceUI(it.raw);
          return {
            id,
            _id: id,
            slug: slug || String(it?.raw?.slug || "").trim(),
            name: it?.name || it?.title || it.raw?.title || "Product",
            img: it?.img || getFirstImageUrl(it.raw?.images) || "/images/placeholder.png",
            price: pr.priceText,
            oldPrice: pr.oldText,
            rating: Number(it?.rating || it.raw?.totalrating || 0),
            raw: it.raw,
          };
        }

        const disc = parseUGX(it?.discountedPrice ?? it?.nowPrice ?? it?.price);
        const old = parseUGX(it?.oldPrice ?? it?.originalPrice);
        const hasDiscount = disc > 0 && old > 0 && disc < old;

        return {
          id,
          _id: id,
          slug,
          name: it?.name || it?.title || "Product",
          img: it?.img || "/images/placeholder.png",
          price: hasDiscount
            ? `UGX (${disc.toLocaleString()}-${old.toLocaleString()})`
            : formatUGX(disc || old),
          oldPrice: "",
          rating: Number(it?.rating || 0),
          raw: it?.raw || null,
        };
      });
  }, [recentlyViewed]);

  const formik = useFormik({
    initialValues: {
      firstname: userState?.firstname || "",
      lastname: userState?.lastname || "",
      email: userState?.email || "",
      mobile: userState?.mobile || "",
    },
    validationSchema: profileSchema,
    onSubmit: async (values) => {
      try {
        await dispatch(updateProfile(values)).unwrap();
        setEdit(true);
      } catch (error) {
        console.error("Profile update failed:", error);
      }
    },
    enableReinitialize: true,
  });

  const handleLogout = useCallback(async () => {
    if (logoutBusy) return;

    try {
      setLogoutBusy(true);
      await dispatch(logoutUser()).unwrap();
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      setLogoutBusy(false);
      navigate("/identify", { replace: true });
    }
  }, [dispatch, navigate, logoutBusy]);

  const initials = useMemo(() => {
    const a = userState?.firstname?.[0] || "";
    const b = userState?.lastname?.[0] || "";
    return `${a}${b}`.toUpperCase() || "U";
  }, [userState]);

  const fullName = useMemo(() => {
    const fn = userState?.firstname || "";
    const ln = userState?.lastname || "";
    const name = `${fn} ${ln}`.trim();
    return name || "User";
  }, [userState]);

  // ✅ click Edit anywhere -> enable + scroll + focus
  const goToEdit = useCallback(() => {
    setEdit(false);

    window.requestAnimationFrame(() => {
      editSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      setTimeout(() => {
        firstNameRef.current?.focus?.();
      }, 250);
    });
  }, []);

  // ✅ slider controls
  const slideBy = useCallback((dir = 1, ref) => {
    const el = ref?.current;
    if (!el) return;
    const amount = Math.max(240, Math.floor(el.clientWidth * 0.85));
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  }, []);

  const isBusy = authLoading || logoutBusy;

  return (
    <div className="pro-page">
      {/* ✅ MOBILE APP HEADER (Alibaba-like) */}
      <section className="pro-appTop">
        <div className="pro-appBar">
          <button
            type="button"
            className="pro-appBack"
            onClick={() => navigate(-1)}
            aria-label="Back"
            disabled={isBusy}
          >
            ‹
          </button>

          <div className="pro-appUser">
            <div className="pro-appAvatar">{initials}</div>
            <div className="pro-appUserText">
              <div className="pro-appName">{fullName}</div>
              <div className="pro-appSmall">{edit ? "Profile view" : "Editing enabled"}</div>
            </div>
          </div>

          <button
            type="button"
            className="pro-appEdit"
            onClick={goToEdit}
            disabled={isBusy}
          >
            Edit
          </button>
        </div>
      </section>

      {/* ✅ PRINT NOW STYLE HEADER (DESKTOP/TABLET) */}
      <section className="pro-bcHero">
        <div className="pro-bcInner">
          <h1 className="pro-bcTitle">My Profile</h1>

          <div className="pro-bcTrail">
            <button type="button" className="pro-bcLink" onClick={() => navigate("/")}>
              Home
            </button>
            <span className="pro-bcSlash">/</span>
            <span className="pro-bcCurrent">My Profile</span>
          </div>
        </div>
      </section>

      {/* ✅ Account Header */}
      <Container class1="wel-back">
        <div className="pro-shell">
          <div className="pro-hero">
            <div className="pro-heroTop">
              <div className="pro-avatar">{initials}</div>

              <div className="pro-heroText">
                <h2 className="pro-title">Account</h2>
                <h4 className="pro-welcome">
                  Welcome <span>{userState?.firstname || "User"}</span>
                </h4>
                <p className="pro-email">{userState?.email}</p>
              </div>

              <div className="pro-actions">
                <button
                  type="button"
                  className={`pro-editBtn ${edit ? "" : "is-active"}`}
                  onClick={goToEdit}
                  aria-label="Edit profile"
                  disabled={isBusy}
                >
                  <FiEdit /> Edit
                </button>
              </div>
            </div>

            <div className="pro-heroMeta">
              <div className="pro-pill">
                <span className="pro-pillKey">Status</span>
                <span className="pro-pillVal">Active</span>
              </div>
              <div className="pro-pill">
                <span className="pro-pillKey">Phone</span>
                <span className="pro-pillVal">{userState?.mobile || "—"}</span>
              </div>
            </div>
          </div>
        </div>
      </Container>

      {/* ✅ QUICK LINKS (Alibaba-like) */}
      <Container class1="sec-11">
        <div className="pro-shell">
          <div className="pro-linksCard">
            <div className="pro-linksHead">
              <h3 className="pro-linksTitle">Essentials</h3>
              <span className="pro-linksSub">Quick access</span>
            </div>

            <div className="pro-linksList">
              {/* ✅ My Orders -> /my-orders */}
              <button
                type="button"
                className="pro-linkRow"
                onClick={() => navigate("/my-orders")}
                disabled={isBusy}
              >
                <span className="pro-linkLeft">
                  <span className="pro-linkIcon">
                    <BsBoxSeam />
                  </span>
                  <span className="pro-linkText">
                    <span className="pro-linkTitle">My Orders</span>
                    <span className="pro-linkDesc">Track and view your orders</span>
                  </span>
                </span>
                <IoChevronForward className="pro-linkChevron" />
              </button>

              {/* ✅ My Favourites -> /whishlist (your route spelling) */}
              <button
                type="button"
                className="pro-linkRow"
                onClick={() => navigate("/whishlist")}
                disabled={isBusy}
              >
                <span className="pro-linkLeft">
                  <span className="pro-linkIcon">
                    <FiHeart />
                  </span>
                  <span className="pro-linkText">
                    <span className="pro-linkTitle">My Favourites</span>
                    <span className="pro-linkDesc">Saved items you love</span>
                  </span>
                </span>
                <IoChevronForward className="pro-linkChevron" />
              </button>

              {/* ✅ Recently Viewed -> /product */}
              <button
                type="button"
                className="pro-linkRow"
                onClick={() => navigate("/product")}
                disabled={isBusy}
              >
                <span className="pro-linkLeft">
                  <span className="pro-linkIcon">
                    <IoEyeOutline />
                  </span>
                  <span className="pro-linkText">
                    <span className="pro-linkTitle">Recently Viewed</span>
                    <span className="pro-linkDesc">Continue where you left off</span>
                  </span>
                </span>
                <IoChevronForward className="pro-linkChevron" />
              </button>
            </div>
          </div>
        </div>
      </Container>

      {/* ✅ RECENTLY VIEWED PRODUCTS */}
      <Container class1="sec-11">
        <div className="pro-shell">
          <div className="pro-rvCard">
            <div className="pro-rvHead">
              <h3 className="pro-rvTitle">Recently Viewed</h3>

              <div className="pro-rvRight">
                <div className="pro-rvNav">
                  <button
                    type="button"
                    className="pro-rvNavBtn"
                    onClick={() => slideBy(-1, rvTrackRef)}
                    aria-label="Previous"
                    disabled={!RECENT_VIEWED_UI.length || isBusy}
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="pro-rvNavBtn"
                    onClick={() => slideBy(1, rvTrackRef)}
                    aria-label="Next"
                    disabled={!RECENT_VIEWED_UI.length || isBusy}
                  >
                    ›
                  </button>
                </div>
              </div>
            </div>

            {RECENT_VIEWED_UI.length ? (
              <div ref={rvTrackRef} className="pro-rvTrack">
                {RECENT_VIEWED_UI.map((item) => {
                  const id = item?.id || item?._id;
                  const slugOrId = item?.slug || id;

                  return (
                    <button
                      key={String(id)}
                      type="button"
                      className="pro-rvItem"
                      onClick={() => slugOrId && navigate(getProductPath(slugOrId))}
                      aria-label={`Open ${item?.name || "product"}`}
                      disabled={isBusy}
                    >
                      <div className="pro-rvImgWrap">
                        <ImgWithLoader
                          src={item?.img}
                          alt={item?.name || "Product"}
                          className="pro-rvImg"
                          wrapClassName="pro-rvImgLoaderWrap"
                          loaderSize={18}
                          imgStyle={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                          style={{ width: "100%", height: "100%" }}
                        />
                      </div>

                      <div className="pro-rvMeta">
                        <div className="pro-rvName" title={item?.name || "Product"}>
                          {item?.name || "Product"}
                        </div>

                        <div className="pro-rvRow">
                          <div className="pro-rvPrice">{item?.price || ""}</div>

                          <div className="pro-rvRating">
                            <span className="pro-rvStar">★</span>
                            <span className="pro-rvScore">
                              {Number(item?.raw?.totalrating ?? item?.rating ?? 0).toFixed(1)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="pro-rvEmpty">No recently viewed products yet.</div>
            )}
          </div>
        </div>
      </Container>

      {/* ✅ Form Card */}
      <Container class1="sec-11">
        <div className="pro-shell">
          <div ref={editSectionRef} className="pro-card">
            <div className="pro-cardHead">
              <div>
                <h3 className="pro-cardTitle">Update Profile</h3>
                <p className="pro-cardSub">Keep your details up to date for smoother checkout.</p>
              </div>

              <FiEdit
                className={`pro-editIcon ${edit ? "" : "is-on"}`}
                onClick={!isBusy ? goToEdit : undefined}
                role="button"
                tabIndex={0}
              />
            </div>

            <form onSubmit={formik.handleSubmit} className="pro-form">
              <div className="pro-grid">
                <div className="pro-field">
                  <label className="pro-label">First Name</label>
                  <input
                    ref={firstNameRef}
                    type="text"
                    name="firstname"
                    disabled={edit || isBusy}
                    className={`pro-input ${formik.touched.firstname && formik.errors.firstname ? "is-error" : ""}`}
                    value={formik.values.firstname}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter first name"
                  />
                  {formik.touched.firstname && formik.errors.firstname ? (
                    <div className="pro-error">{formik.errors.firstname}</div>
                  ) : null}
                </div>

                <div className="pro-field">
                  <label className="pro-label">Last Name</label>
                  <input
                    type="text"
                    name="lastname"
                    disabled={edit || isBusy}
                    className={`pro-input ${formik.touched.lastname && formik.errors.lastname ? "is-error" : ""}`}
                    value={formik.values.lastname}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter last name"
                  />
                  {formik.touched.lastname && formik.errors.lastname ? (
                    <div className="pro-error">{formik.errors.lastname}</div>
                  ) : null}
                </div>

                <div className="pro-field pro-span2">
                  <label className="pro-label">Email address</label>
                  <input
                    type="email"
                    name="email"
                    disabled={edit || isBusy}
                    className={`pro-input ${formik.touched.email && formik.errors.email ? "is-error" : ""}`}
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="your@email.com"
                  />
                  {formik.touched.email && formik.errors.email ? (
                    <div className="pro-error">{formik.errors.email}</div>
                  ) : null}
                </div>

                <div className="pro-field pro-span2">
                  <label className="pro-label">Mobile Number</label>
                  <input
                    type="tel"
                    name="mobile"
                    disabled={edit || isBusy}
                    className={`pro-input ${formik.touched.mobile && formik.errors.mobile ? "is-error" : ""}`}
                    value={formik.values.mobile}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="+256 7xx xxx xxx"
                  />
                  {formik.touched.mobile && formik.errors.mobile ? (
                    <div className="pro-error">{formik.errors.mobile}</div>
                  ) : null}
                </div>
              </div>

              <div className="pro-footer">
                <button
                  type="button"
                  className="pro-logout"
                  onClick={handleLogout}
                  disabled={isBusy}
                >
                  {logoutBusy ? "LOGGING OUT..." : "LOGOUT"}
                </button>

                {!edit ? (
                  <button type="submit" className="pro-save" disabled={isBusy}>
                    {authLoading && !logoutBusy ? "Saving..." : "Save Changes"}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="pro-save pro-save--ghost"
                    onClick={goToEdit}
                    disabled={isBusy}
                  >
                    Enable Editing
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default Profile;